<template>
	<v-dialog v-model="showDialog" max-width="650">
		<v-card class="d-flex flex-column" style="max-height: 80vh">
			<v-toolbar color="primary" dark flat>
				<v-toolbar-title>Move to tag group</v-toolbar-title>
				<v-hover-icon class="mr-3" />
			</v-toolbar>

			<div class="px-4 pt-4 flex-shrink-0">
				<p class="text-body-2 mb-2">
					Move data from
					<strong>{{ sourceTagGroup ? sourceTagGroup.name : "" }}</strong>
					to another tag group. Tags absent from the target are dropped.
				</p>
				<v-select
					:model-value="selectedTargetUuid"
					:items="targetOptions"
					item-title="name"
					item-value="uuid"
					label="Target tag group"
					density="compact"
					variant="outlined"
					hide-details
					class="mb-2"
					:menu-props="{ transition: false }"
					@update:model-value="onTargetChanged"
				/>
			</div>

			<div
				v-if="tagsWithData.length > 0 && selectedTargetUuid"
				class="d-flex flex-column overflow-hidden"
			>
				<div class="d-flex align-center px-4 mb-2">
					<p class="text-body-2 font-weight-medium">
						Tags with data ({{ tagsWithData.length }}):
					</p>
				</div>
				<div
					class="px-4 d-flex flex-column overflow-hidden flex-grow-1"
					style="min-height: 0"
				>
					<div class="overflow-y-auto tag-table-wrapper rounded">
						<v-table density="compact">
							<thead>
								<tr>
									<th class="text-left" style="width: 40%">Tag</th>
									<th class="text-left" style="width: 35%">Value</th>
									<th class="text-left" style="width: 25%">Action</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="tag in sortedTags"
									:key="tag.uuid"
									:class="{
										'text-fg-2': !canMove(tag.uuid),
									}"
								>
									<td class="text-body-2">
										<v-tooltip
											:text="tag.name"
											:disabled="tag.name.length <= 28"
											:open-delay="400"
											location="top"
										>
											<template #activator="{ props }">
												<span v-bind="props">{{ truncateTag(tag.name) }}</span>
											</template>
										</v-tooltip>
									</td>
									<td class="text-body-2">
										<v-tooltip
											:text="tagValueFull(tag.uuid)"
											:disabled="tagValueFull(tag.uuid).length <= 24"
											:open-delay="400"
											location="top"
										>
											<template #activator="{ props }">
												<span v-bind="props">{{
													tagValuePreview(tag.uuid)
												}}</span>
											</template>
										</v-tooltip>
									</td>
									<td>
										<v-btn-toggle
											:model-value="tagActions[tag.uuid]"
											mandatory
											density="compact"
											variant="outlined"
											@update:model-value="setAction(tag.uuid, $event)"
										>
											<v-btn
												value="move"
												size="x-small"
												:ripple="false"
												:disabled="!canMove(tag.uuid)"
												:class="{
													'action-move-active': tagActions[tag.uuid] === 'move',
												}"
											>
												Move
											</v-btn>
											<v-btn
												value="drop"
												size="x-small"
												:ripple="false"
												:class="{
													'action-drop-active': tagActions[tag.uuid] === 'drop',
												}"
											>
												Drop
											</v-btn>
										</v-btn-toggle>
									</td>
								</tr>
							</tbody>
						</v-table>
					</div>
				</div>
				<div
					class="text-caption text-fg-2 mt-3 px-4 pb-2 flex-shrink-0"
					style="
						display: grid;
						grid-template-columns: auto auto 1fr;
						column-gap: 4px;
					"
				>
					<strong>Move</strong> <span>-</span>
					<span>
						transfer the tag and value to the target tag group (only available
						if the tag exists in the target).
					</span>
					<strong>Drop</strong> <span>-</span>
					<span>
						permanently delete the tag and value (not kept in the source tag
						group).
					</span>
				</div>
			</div>

			<div v-if="tagsWithData.length === 0" class="px-4 pb-2">
				<v-alert
					icon="mdi-information"
					density="compact"
					class="bg-valence-info-tint text-valence-info-ink"
				>
					This tag group instance has no data to move.
				</v-alert>
			</div>

			<div v-if="selectedTargetUuid" class="px-4 pb-2">
				<v-alert
					icon="mdi-alert-circle"
					density="compact"
					class="mb-0 bg-valence-attention-tint text-valence-attention-ink"
				>
					<div class="text-body-2">{{ summaryLine }}</div>
					<div class="text-caption mt-1">This action cannot be undone.</div>
				</v-alert>
			</div>

			<v-card-actions class="px-4 pb-4">
				<v-spacer />
				<v-btn variant="text" @click="cancel"> Cancel </v-btn>
				<v-btn
					class="bg-secondary"
					elevation="2"
					:disabled="!canConfirm"
					@click="confirm"
				>
					Confirm
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import VHoverIcon from "@/components/VHoverIcon";
import utils from "@/utils";
import pluralize from "@/utils/pluralize";

export default {
	name: "SwitchTagGroupDialog",

	components: {
		VHoverIcon,
	},

	data() {
		return {
			showDialog: false,
			sourceTagGroup: null,
			// [{ uuid, name, values: [string, ...] }] - one entry per distinct tag
			// that currently has data in the source instance.
			tagsWithData: [],
			// Full list of tag groups available to the paper. Each entry must have
			// { uuid, name, tags: [{ uuid, name }, ...] }.
			tagGroups: [],
			// Names of "key" tag groups (Study, Study part, ...) to exclude from
			// the target dropdown. Passed in so the dialog stays decoupled from
			// the WPD plugin's classification helpers.
			keyTagGroupNames: [],

			selectedTargetUuid: null,
			tagActions: {},

			// Resolver stashed at open(); resolves the promise returned to the
			// caller. Resolved with the confirm payload, or null on cancel.
			resolveResult: null,
		};
	},

	computed: {
		recordTagUuids() {
			return new Set(this.tagsWithData.map((t) => t.uuid));
		},

		targetOptions() {
			if (!this.sourceTagGroup) return [];
			const keyNames = new Set(this.keyTagGroupNames);
			return this.tagGroups.filter((tg) => {
				if (tg.uuid === this.sourceTagGroup.uuid) return false;
				if (keyNames.has(tg.name)) return false;
				const tgTagUuids = (tg.tags || []).map((t) => t.uuid);
				return tgTagUuids.some((uuid) => this.recordTagUuids.has(uuid));
			});
		},

		selectedTarget() {
			if (!this.selectedTargetUuid) return null;
			return this.tagGroups.find((tg) => tg.uuid === this.selectedTargetUuid);
		},

		targetTagUuids() {
			if (!this.selectedTarget) return new Set();
			return new Set((this.selectedTarget.tags || []).map((t) => t.uuid));
		},

		sortedTags() {
			return [...this.tagsWithData].sort((a, b) => {
				const aMovable = this.targetTagUuids.has(a.uuid) ? 0 : 1;
				const bMovable = this.targetTagUuids.has(b.uuid) ? 0 : 1;
				return aMovable - bMovable;
			});
		},

		moveCount() {
			return Object.values(this.tagActions).filter((a) => a === "move").length;
		},

		dropCount() {
			return Object.values(this.tagActions).filter((a) => a === "drop").length;
		},

		summaryLine() {
			const target = this.selectedTarget?.name ?? "the target";
			const source = this.sourceTagGroup?.name ?? "the source";
			const movePart = `${pluralize("tag", this.moveCount, true)} will be moved to ${target}.`;
			const dropPart =
				this.dropCount > 0
					? ` ${pluralize("tag", this.dropCount, true)} will be permanently deleted.`
					: "";
			const sourcePart = ` The source instance under ${source} will be removed.`;
			return movePart + dropPart + sourcePart;
		},

		canConfirm() {
			return (
				this.selectedTargetUuid &&
				this.tagsWithData.length > 0 &&
				this.moveCount > 0
			);
		},
	},

	watch: {
		// When the dialog is closed via outside-click / Escape, treat it as a
		// cancel and resolve with null so callers don't hang.
		showDialog(open) {
			if (!open && this.resolveResult) {
				const resolve = this.resolveResult;
				this.resolveResult = null;
				resolve(null);
			}
		},
	},

	methods: {
		open({ sourceTagGroup, tagsWithData, tagGroups, keyTagGroupNames }) {
			this.sourceTagGroup = sourceTagGroup ?? null;
			this.tagsWithData = tagsWithData ?? [];
			this.tagGroups = tagGroups ?? [];
			this.keyTagGroupNames = keyTagGroupNames ?? [];
			this.selectedTargetUuid = null;
			this.tagActions = {};
			this.showDialog = true;

			return new Promise((resolve) => {
				this.resolveResult = resolve;
			});
		},

		close() {
			this.showDialog = false;
		},

		onTargetChanged(uuid) {
			this.selectedTargetUuid = uuid;
			this.resetActions();
		},

		canMove(tagUuid) {
			return this.targetTagUuids.has(tagUuid);
		},

		resetActions() {
			const actions = {};
			for (const tag of this.tagsWithData) {
				actions[tag.uuid] = this.canMove(tag.uuid) ? "move" : "drop";
			}
			this.tagActions = actions;
		},

		setAction(tagUuid, action) {
			this.tagActions = { ...this.tagActions, [tagUuid]: action };
		},

		truncateTag(name) {
			return utils.truncateText(name, 28);
		},

		tagValueFull(tagUuid) {
			const tag = this.tagsWithData.find((t) => t.uuid === tagUuid);
			return (tag?.values ?? []).filter(Boolean).join(", ");
		},

		tagValuePreview(tagUuid) {
			return utils.truncateText(this.tagValueFull(tagUuid), 24);
		},

		confirm() {
			const payload = {
				targetTagGroup: this.selectedTarget,
				tagActions: { ...this.tagActions },
			};
			const resolve = this.resolveResult;
			this.resolveResult = null;
			this.showDialog = false;
			if (resolve) resolve(payload);
		},

		cancel() {
			const resolve = this.resolveResult;
			this.resolveResult = null;
			this.showDialog = false;
			if (resolve) resolve(null);
		},
	},
};
</script>

<style scoped>
.action-move-active {
	background-color: rgb(var(--v-theme-secondary)) !important;
	color: rgb(var(--v-theme-on-secondary)) !important;
}

.action-drop-active {
	background-color: #e57373 !important;
	color: white !important;
}

:deep(.v-btn-toggle) {
	border: 1px solid var(--border-default);
	border-radius: 4px;
}

:deep(.v-btn-toggle > .v-btn) {
	border: none !important;
	border-right: 1px solid var(--border-default) !important;
	border-radius: 0 !important;
	transition: none !important;
}

:deep(.v-btn-toggle > .v-btn:last-child) {
	border-right: none !important;
}

:deep(.v-btn-toggle > .v-btn--disabled) {
	opacity: 0.4 !important;
}

.tag-table-wrapper {
	border: 1px solid var(--border-default);
}

:deep(.v-table tbody tr + tr) {
	border-top: 4px solid transparent;
}
</style>
